import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    Output,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {ContentService} from "../../_services/content.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {DomSanitizer, SafeResourceUrl} from "@angular/platform-browser";
import {videoLinkUpload} from '../../_utils/video-link-sanitizer';
import {ImageFile, PostAttachment} from '../../_types/content';
import {Heatmap, Poll} from '../../_types/polls';
import {AuthService} from '../../_services/auth.service';
import {Community, UserAccessLevel} from '../../_types/communities';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {PollsService} from '../../_services/polls.service';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {ConfirmationDialogComponent} from '../confirmation-dialog/confirmation-dialog.component';
import {ConfirmationDialogData} from '../../_types/shared';
import {SnackbarService} from '../../_services/snackbar.service';
import {CreatePollComponent} from '../create-poll/create-poll.component';
import {MatButtonToggleGroup} from "@angular/material/button-toggle";
import {LocalStorageService} from "../../_services/local-storage.service";
import {FileInputValidators} from "@ngx-dropzone/cdk";
import {CommunitiesService} from "../../_services/communities.service";

function maxFilesValidator(max: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const v = control.value;

        // normalize to File[]
        const files: File[] =
            !v ? [] :
                v instanceof File ? [v] :
                    Array.from(v as FileList | File[]);

        return files.length > max
            ? {maxFiles: {requiredLength: max, actualLength: files.length}}
            : null;
    };
}

@Component({
    selector: 'app-attachments',
    templateUrl: './attachments.component.html',
    styleUrl: './attachments.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AttachmentsComponent implements AfterViewInit {
    community: Community | null = null;
    @Input() attachmentButtonText?: string | null;
    @Input() voteButtonText?: string | null;
    @ViewChild(CreatePollComponent, {static: false}) createPollComponent?: CreatePollComponent;
    @ViewChild('group', {static: false}) group!: MatButtonToggleGroup;
    @ViewChild('videoInput') videoInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('audioInput') audioInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('imageInput') imageInputRef!: ElementRef<HTMLInputElement>;

    @Input() attachmentOptions: string [] = ['text', 'image', 'video', 'audio', 'videoLink', 'screenRecording', 'doc', 'gif', 'survey_url', 'poll', 'heatmap', 'ai_comment_builder']
    @Input() commentType: string = 'text'
    @Input() comment: boolean = false;
    @Input() displayOverText: boolean = false;
    @Input() small: boolean = false;
    @Input() hideTypeChoosing: boolean = false;
    @Input() disableCarousel: boolean = false;
    @Input() reply: boolean = false;
    @Input() disable: boolean = false;
    @Output() isValid: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() imagePositionChange: EventEmitter<boolean> = new EventEmitter<boolean>();
    @Output() submitField: EventEmitter<any> = new EventEmitter<any>();
    @Output() removedAttachment: EventEmitter<'poll' | 'heatmap'> = new EventEmitter<'poll' | 'heatmap'>();
    @Output() updatedPoll: EventEmitter<void> = new EventEmitter<void>();
    @Output() inUse: EventEmitter<boolean> = new EventEmitter<boolean>();
    compressableImageTypes: string[] = ['bmp', 'webp', 'png', 'jpeg', 'jpg']
    url: string = '(((ftp|http|https):\\/\\/)|(\\/)|(..\\/))(\\w+:{0,1}\\w*@)?(\\S+)(:[0-9]+)?(\\/|\\/([\\w#!:.?+=&%@!\\-\\/]))?';
    gifs: any[] = [];
    page: number = 1;
    limit: number = 15;
    resultsLength: number = 0;
    maxFilesize: number = 100;
    imageCtrl: FormControl = new FormControl(null, [
        Validators.required,
        // Read the current community limit instead of capturing the initial
        // 100 MiB default before the community has loaded.
        (control: AbstractControl) => this.hasUploadSizeLimit
            ? FileInputValidators.maxSize(this.maxFilesize * 1024 * 1024)(control) : null,
        FileInputValidators.accept('.jpg, .JPG, .jpeg, .png, .bmp, .svg, .webp'),
        maxFilesValidator(10) // Limit to 10 files
    ]);
    images: any[] = [];
    private imageSelectionVersion = 0;
    private readonly initialImageFiles = new Map<File, number>();
    videoCtrl: FormControl<any> = new FormControl(null, Validators.required);
    video: any;
    audioCtrl: FormControl<any> = new FormControl(null, Validators.required);
    audio: any;
    videoLinkFormControl: FormControl<string | null> = this.fb.control(null, [Validators.pattern(this.url), Validators.required]);
    linkFormControl: FormControl<any> = this.fb.control(null, [Validators.pattern(this.url), Validators.required]);
    surveyFormControl: FormControl<any> = this.fb.control(null, [Validators.pattern(this.url), Validators.required]);
    docTitle: FormControl<any> = this.fb.control(null);
    docCtrl: FormControl<any> = new FormControl(null, Validators.required);
    doc: any;
    gif: any;
    videoLink: any;
    fileSize: number = 0;
    maxScreenRecordingFilesize: number = 512;
    recording: boolean = false;
    screenRecording: any;
    mediaRecorder: any;
    stream: any;
    screenRecordingUrl: any;
    isLoaded: boolean = true;
    initialFile: any;
    initialHeatmap?: Heatmap;
    initialPoll?: Poll;
    safeImageLink?: SafeResourceUrl;
    isEditPoll: boolean = false;
    heatMapEditMode: boolean = false;
    isApp: boolean = this.localStorageService.getItem('isApp', false);
    isMobile: "ANDROID" | "IOS" | null = null;
    isMobileSize: boolean = false;


    constructor(private readonly fb: FormBuilder,
                private readonly contentService: ContentService,
                private readonly snackBar: MatSnackBar,
                private domSanitizer: DomSanitizer, private authService: AuthService,
                private pollsService: PollsService, private dialog: MatDialog,
                private snackbarService: SnackbarService,
                private communitiesService: CommunitiesService,
                private readonly localStorageService: LocalStorageService,
                private readonly cdr: ChangeDetectorRef) {
        this.communitiesService.getCurrentDashboardCommunity().pipe(takeUntilDestroyed()).subscribe(community => {
            this.community = community;
            this.maxFilesize = community?.upload_size_limit_mb ?? 100;
            this.imageCtrl.updateValueAndValidity({emitEvent: false});
        })

        if (/Android/i.test(navigator.userAgent)) {
            this.isMobile = "ANDROID";
        } else if (/iPad|iPhone|iPod/i.test(navigator.userAgent)) {
            this.isMobile = "IOS";
        }
        if (!this.attachmentOptions.includes(this.commentType)) {
            this.commentType = this.attachmentOptions[0];
        }
        this.imageCtrl.valueChanges.subscribe(async (selectedValue): Promise<void> => {
            const version = ++this.imageSelectionVersion;
            this.isValid.emit(false);
            this.images = [];
            const selectedFiles = Array.isArray(selectedValue)
                ? selectedValue
                : selectedValue instanceof File
                    ? [selectedValue]
                    : Array.from((selectedValue ?? []) as FileList);
            if (selectedFiles.length > 10) {
                selectedValue = selectedFiles.slice(0, 10);
                this.imageCtrl.setValue(selectedValue, {emitEvent: false});
                this.openSnackBar('Some images have not be uploaded you can only select up to 10 files.', 'error');
            }
            if (this.imageCtrl.invalid || !selectedValue) {
                this.isValid.emit(false); // Emit invalid immediately
                return;
            }
            let files: { type: 'image', value: any[], initial: boolean, retainedImageIds: number[] } = {
                type: 'image',
                value: [],
                initial: selectedValue === this.initialFile,
                retainedImageIds: [],
            }
            let valid: boolean = true;
            const filesArray = Array.isArray(selectedValue)
                ? selectedValue
                : selectedValue instanceof File
                    ? [selectedValue]
                    : Array.from(selectedValue as FileList);

            for (const file of filesArray) {
                const mimeType = file?.type;
                if (mimeType && mimeType.match(/image\/*/) == null) {
                    this.openSnackBar("Only images are supported.", 'error');
                    return;
                }

                let compressedFile;
                if (!this.initialImageFiles.has(file) && this.hasFileType(mimeType, this.compressableImageTypes)) {
                    const imageCompression = (await import('browser-image-compression')).default;
                    try {
                        compressedFile = await imageCompression(file, {
                            maxWidthOrHeight: 2000,
                            useWebWorker: true,
                            fileType: 'image/jpeg'
                        });
                        compressedFile = new File([compressedFile], "compressed.jpeg", {type: 'image/jpeg'});
                        const savedKB: number = (file.size - compressedFile.size) / 1024;
                        console.log(`Kb Saved: ${savedKB} (${(savedKB / (file.size / 1024)) * 100}%)`);
                    } catch (error) {
                        console.error(error);
                    }
                }

                if (version !== this.imageSelectionVersion) return;
                const reader: FileReader = new FileReader();
                this.fileSize = file.size / (1024 * 1024);

                reader.readAsDataURL(file);
                reader.onload = (_event): void => {
                    if (version !== this.imageSelectionVersion) return;
                    if (this.isWithinUploadLimit(file)) {
                        this.images.push(reader.result);
                        this.cdr.detectChanges();
                    }
                }

                if (this.isWithinUploadLimit(file)) {
                    const initialId = this.initialImageFiles.get(file);
                    if (initialId !== undefined) {
                        files.retainedImageIds.push(initialId);
                    } else {
                        files.value.push(compressedFile ? this.ensureFileNameIsCorrectSize(compressedFile) : this.ensureFileNameIsCorrectSize(file));
                    }
                } else {
                    valid = false;
                }
            }
            if (version !== this.imageSelectionVersion) return;
            this.isValid.emit(valid);
            if (this.disableCarousel) {
                files.value = files.value[0]
            }
            this.submitField.emit(files)
        })


        this.videoCtrl.valueChanges.subscribe((selectedValue): void => {
            this.video = null;
            this.isValid.emit(false);
            if (!selectedValue) {
                this.submitField.emit({type: 'video', value: null});
                return;
            }
            const mimeType = selectedValue?.type;
            if (mimeType && mimeType?.match(/video\/*/) == null) {
                this.openSnackBar("Only videos are supported.", 'error');
                return;
            }

            this.fileSize = selectedValue.size / (1024 * 1024);

            if (this.isWithinUploadLimit(selectedValue)) {
                this.video = URL.createObjectURL(selectedValue);
                this.isValid.emit(true);
                this.submitField.emit({
                    type: 'video',
                    value: this.ensureFileNameIsCorrectSize(selectedValue),
                    initial: selectedValue === this.initialFile
                })
            } else {
                this.submitField.emit({type: 'video', value: null});
            }

        })
        this.audioCtrl.valueChanges.subscribe((selectedValue): void => {
            this.audio = null;
            this.isValid.emit(false);
            if (!selectedValue) {
                this.submitField.emit({type: 'audio', value: null});
                return;
            }
            const mimeType = selectedValue?.type;
            if (mimeType && mimeType.match(/audio\/*/) == null) {
                this.openSnackBar("Only audio files are supported.", 'error');
                return;
            }

            this.fileSize = selectedValue.size / (1024 * 1024);
            if (this.isWithinUploadLimit(selectedValue)) {
                this.audio = URL.createObjectURL(selectedValue);
                this.isValid.emit(true);
                this.submitField.emit({
                    type: 'audio',
                    value: this.ensureFileNameIsCorrectSize(selectedValue),
                    initial: selectedValue === this.initialFile
                })
            } else {
                this.submitField.emit({type: 'audio', value: null});
            }

        })


        this.docCtrl.valueChanges.subscribe((selectedValue): void => {
            this.doc = selectedValue?.name;
            this.fileSize = (selectedValue?.size ?? 0) / (1024 * 1024);

            const valid = !!selectedValue && this.isWithinUploadLimit(selectedValue);
            this.isValid.emit(valid);
            this.submitField.emit({
                type: 'document',
                value: valid ? this.ensureFileNameIsCorrectSize(selectedValue) : null,
                initial: selectedValue === this.initialFile
            })
            this.cdr.detectChanges();
        })

        this.surveyFormControl.valueChanges.subscribe((selectedValue): void => {
            this.submitField.emit({
                type: 'survey_url',
                value: selectedValue,
                initial: selectedValue === this.initialFile
            })

        })
    }

    @Input() set initialAttachment(value: PostAttachment | Heatmap | Poll | undefined | null | ImageFile[]) {

        if (!value) return;

        if (Array.isArray(value)) {
            let images: ImageFile[] = [...value].reverse();
            this.commentType = 'image';
            const fetchPromises: Promise<File>[] = images.map(image => this.contentService.fetchFile(image.file));
            Promise.all(fetchPromises).then((files: File[]): void => {
                this.isLoaded = true;
                this.initialFile = files;
                files.forEach((file, index) => this.initialImageFiles.set(file, images[index].id));
                this.imageCtrl.setValue(files);
            }).catch(err => {
                console.error("Error loading initial images", err);
            });
        } else {
            if (!value.id) return;
            this.storeInitialAttachmentForEditing(value);
        }
    };

    get isSuperuser(): boolean {
        return this.authService.isSuperUser();
    }

    get hasUploadSizeLimit(): boolean {
        return !this.authService.isSuperUser() && !this.authService.isStaff();
    }

    private isWithinUploadLimit(file: File): boolean {
        return !this.hasUploadSizeLimit || file.size <= this.maxFilesize * 1024 * 1024;
    }

    @HostListener('window:resize', ['$event'])
    onResize(event: any): void {
        this.isMobileSize = window.innerWidth <= 1000;
    }

    ngAfterViewInit(): void {
        this.isMobileSize = window.innerWidth <= 1000;
    }

    ensureFileNameIsCorrectSize(file: File): any {
        // API only allows files with less than 100 char filename
        let fileToSubmit: File = file;

        function getFileExtension(filename: string): string {
            const parts = filename.split('.');
            return parts.length > 1 ? `.${parts.pop()}` : '';
        }

        // Check if the filename length exceeds 100 characters
        if (file.name.length > 100) {
            const originalExtension: string = getFileExtension(file.name);
            const desiredFileName = `upload${originalExtension}`;
            // Create a new File object with the desired name
            fileToSubmit = new File([file], desiredFileName, {type: file.type});
        }
        return fileToSubmit;
    }

    hasCommunityRole(roles: UserAccessLevel[]): boolean {
        return this.communitiesService.userHasCommunityRole(roles);
    }

    onToggleGroupChange(event: any): void {
        this.imageSelectionVersion++;
        this.inUse.emit(event.value !== 'text')
        this.fileSize = 0;
        this.isValid.emit(false);
        if (this.initialPoll && event.value && event.value != 'poll') {
            this.removePoll();
        } else if (this.initialHeatmap && event.value && event.value != 'heatmap') {
            this.removeHeatmap();
        }
        this.submitField.emit({type: event.value, value: null, initial: false});
        switch (event.value) {
            case 'image':
                this.checkValidity(this.imageCtrl, event.value);
                if (this.isApp) {
                    setTimeout((): void => {
                        this.imageInputRef?.nativeElement.click();
                    }, 100);
                }
                break;
            case 'video':
                this.checkValidity(this.videoCtrl, event.value);
                if (this.isApp) {
                    setTimeout((): void => {
                        this.videoInputRef?.nativeElement.click();
                    }, 100);
                }
                break;
            case 'audio':
                this.checkValidity(this.audioCtrl, event.value);
                if (this.isApp) {
                    setTimeout((): void => {
                        this.audioInputRef?.nativeElement.click();
                    }, 100);
                }
                break;
            case 'videoLink':
                this.checkValidity(this.videoLinkFormControl, 'videoLink')
                break;
            case 'screenRecording':
                this.checkValidity(this.videoLinkFormControl, 'screenRecording')
                break;
            case 'doc':
                this.checkValidity(this.docCtrl, 'document');
                break;
            case 'gif':
                if (this.gif) this.selectGif(this.gif);
                break;
        }
    }

    checkValidity(formControl: FormControl, fieldType: string): void {
        if (formControl.valid) {
            this.isValid.emit(true);
            this.submitField.emit({
                type: fieldType,
                value: formControl.value,
                initial: formControl.value === this.initialFile
            })
        } else {
            this.isValid.emit(false);
        }
        this.cdr.detectChanges();
    }

    searchGifs(searchTerm: string, page?: number): void {
        this.contentService.searchGifs(searchTerm, this.limit, this.page).subscribe(r => {
            this.gifs = r.data;
            this.resultsLength = r.pagination.total_count;

        }, e => {
            this.openSnackBar(e, 'error')
        })
    }

    handlePageEvent(event: any, search: string): void {
        this.page = event.pageIndex + 1;
        this.searchGifs(search, this.page);
    }

    selectGif(resultGif: any): void {
        this.isValid.emit(true);
        this.gif = resultGif;
        this.submitField.emit({
            type: 'gif',
            value: resultGif,
            initial: this.gif.embed_url === this.initialFile?.embed_url
        })
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }

    async startScreenRecording(audio?: boolean): Promise<void> {
        if (audio) {
            this.stream = await this.recordScreenWithNarration()
        } else {
            this.stream = await this.recordScreen();
        }
        this.recording = true;
        this.mediaRecorder = this.createRecorder(this.stream);
    }

    stopScreenRecording(): void {
        this.recording = false;
        this.mediaRecorder.stop();
        this.stream?.getTracks() // get all tracks from the MediaStream
            .forEach((track: any) => track.stop()); // stop each of them
        this.stream = null;
    }

    async recordScreen(): Promise<MediaStream> {
        return await navigator.mediaDevices.getDisplayMedia({
            audio: true,
            video: {
                width: {max: 1920}, height: {max: 1080}
            },
            // @ts-ignore: selfBrowserSurface is not yet in DisplayMediaStreamOptions type definitions
            selfBrowserSurface: "include",
            systemAudio: "include",
            surfaceSwitching: "include",
            monitorTypeSurfaces: "include",
        });
    }

    async recordScreenWithNarration(): Promise<MediaStream> {
        let screenStream: MediaStream | null = null;
        let microphoneStream: MediaStream | null = null;
        let combinedStream: MediaStream | null = null;

        try {
            microphoneStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
                video: false
            });

            if (!microphoneStream || microphoneStream.getAudioTracks().length === 0) {
                console.warn('Microphone stream acquired but no audio tracks found, or permission denied.');
                this.snackbarService.openErrorSnackBar("Microphone not detected or accessible. Please check your system settings and browser permissions.");
                throw new Error('Microphone access failed or no audio tracks available.');
            }
            screenStream = await navigator.mediaDevices.getDisplayMedia({
                audio: false,
                video: {
                    width: {max: 1920},
                    height: {max: 1080}
                },
                // @ts-ignore: selfBrowserSurface is not yet in DisplayMediaStreamOptions type definitions
                selfBrowserSurface: "include",
                surfaceSwitching: "include",
                monitorTypeSurfaces: "include",
            });

            if (!screenStream) {
                console.error('Screen stream could not be acquired.');
                throw new Error('Screen capture failed.');
            }
            combinedStream = new MediaStream();
            screenStream.getVideoTracks().forEach(track => combinedStream!.addTrack(track));
            // Add all audio tracks from the microphone stream
            microphoneStream.getAudioTracks().forEach(track => combinedStream!.addTrack(track));
            return combinedStream;
        } catch (error) {
            console.error("Error recording screen with narration (without system audio):", error);

            if (error instanceof DOMException) {
                if (error.name === "NotAllowedError") {
                    this.snackbarService.openErrorSnackBar("Permission denied. Please allow microphone and screen sharing access.");
                } else if (error.name === "NotFoundError") {
                    this.snackbarService.openErrorSnackBar("No microphone or screen found. Please ensure your devices are connected and configured.");
                } else if (error.name === "AbortError") {
                    this.snackbarService.openErrorSnackBar("Screen recording with narration was cancelled.");
                } else {
                    this.snackbarService.openErrorSnackBar(`Recording error: ${error.message || error || 'An unknown error occurred.'}`);
                }
            } else {
                const errorMessage: string = (error as Error).message || "An unknown error occurred.";
                this.snackbarService.openErrorSnackBar(`Failed to start screen recording with narration: ${errorMessage}. Please try again.`);
            }
            screenStream?.getTracks().forEach(track => track.stop());
            microphoneStream?.getTracks().forEach(track => track.stop());
            throw error;
        }
    }

    createRecorder(stream: MediaStream): MediaRecorder {
        // the stream data is stored in this array
        let recordedChunks: Blob[] = [];

        const mediaRecorder = new MediaRecorder(stream);
        mediaRecorder.ondataavailable = function (e): void {
            if (e.data.size > 0) {
                recordedChunks.push(e.data);
            }
        };
        let that = this
        mediaRecorder.addEventListener('stop', event => {
            const blob: Blob = new Blob(recordedChunks, {
                type: 'video/x-matroska'
            });
            that.screenRecording = blob;
            that.screenRecordingUrl = URL.createObjectURL(blob);
            const file: File = new File([blob], `recording_${new Date().getTime()}.mkv`, {type: 'video/x-matroska'});
            this.fileSize = +(Math.round(+file.size / 1024) / 1000).toFixed(2)
            if (this.fileSize < this.maxScreenRecordingFilesize) {
                this.isValid.emit(true);
            } else {
                this.openSnackBar("Screen recording is too large.", 'error');

            }
            this.submitField.emit({type: 'video', value: file});
            recordedChunks = [];
            this.stopScreenRecording();
        });

        mediaRecorder.start(200); // For every 200ms the stream data will be stored in a separate chunk.
        return mediaRecorder;
    }

    videoLinkUpload(link: string | null): void {
        if (link) {
            this.videoLink = videoLinkUpload(link, this.domSanitizer);
        }
    }

    editPoll(): void {
        this.isEditPoll = true;
    }

    pollUpdated(poll: Poll): void {
        this.initialPoll = poll;
        this.commentType = 'poll';
        this.isValid.emit(true);
        this.isEditPoll = false;
        this.updatedPoll.emit();
    }

    removePoll(): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to delete this Poll? <br>(<i>Please note</i>: all votes will also be deleted)',
                title: 'Delete poll',
                showHeader: true
            }
        });
        dialogRef.afterClosed().subscribe(r => {
            if (r) {
                this.pollsService.deletePoll(this.initialPoll!.id).subscribe(r => {
                        this.removedAttachment.emit('poll');
                        this.initialPoll = undefined;
                        this.commentType = 'text';
                        this.snackbarService.openSuccessSnackBar('Heatmap has been successfully deleted.');
                    },
                    e => {
                        this.snackbarService.openErrorSnackBar(e);
                    }
                )
            } else {
                this.commentType = 'poll'
            }
        })

    }

    removeHeatmap(): void {
        const dialogRef: MatDialogRef<ConfirmationDialogComponent> = this.dialog.open<ConfirmationDialogComponent, ConfirmationDialogData>(ConfirmationDialogComponent, {
            panelClass: 'mediumWindow',
            data: {
                showSubmitBtn: true,
                showCancelBtn: true,
                message: 'Are you sure you want to delete this Heatmap? <br>(<i>Please note</i>: all votes will also be deleted)',
                title: 'Delete heatmap',
                showHeader: true
            }
        });
        dialogRef.afterClosed().subscribe(r => {
            if (r) {
                this.pollsService.deleteHeatmap(this.initialHeatmap!.id).subscribe(r => {
                        this.removedAttachment.emit('heatmap');
                        this.initialHeatmap = undefined;
                        this.commentType = 'text';
                        this.snackbarService.openSuccessSnackBar('Heatmap has been successfully deleted.');
                    },
                    e => {
                        this.snackbarService.openErrorSnackBar(e);
                    })
            } else {
                this.commentType = 'heatmap';
            }
        })
    }

    reset() {
        this.imageSelectionVersion++;
        this.submitField.emit({
            type: 'text',
            value: null,
            initial: !this.initialFile
        });
        this.isValid.emit(true);
    }

    changeHeatmapEndDate() {
        this.heatMapEditMode = true;
    }

    hasFileType(fileType: string, typeList: string[]): boolean {
        let included: boolean = false;
        for (let type of typeList) {
            if (fileType && fileType?.toLowerCase().includes(type)) {
                included = true;
            }
        }
        return included
    }

    private storeInitialAttachmentForEditing(value: PostAttachment | Heatmap | Poll): void {
        this.isValid.emit(false);
        this.isLoaded = false;
        const isPostAttachment = (v: PostAttachment | Heatmap | Poll): v is PostAttachment => {
            return !!(v as PostAttachment)?.attachment_type;
        }
        const isPoll = (v: PostAttachment | Heatmap | Poll): v is Poll => {
            return !!(v as Poll)?.questions;
        }
        if (isPostAttachment(value)) {
            if (value?.attachment_type === 'image' || value?.attachment_type === 'video' || value?.attachment_type === 'document' || value?.attachment_type === 'audio') {
                this.contentService.fetchFile(value?.file).then((res): void => {
                    this.initialFile = res;
                    this.commentType = value?.attachment_type;
                    switch (this.commentType) {
                        case 'image':
                            this.imageCtrl.setValue(res);
                            break;
                        case 'video':
                            this.videoCtrl.setValue(res);
                            break;
                        case 'audio':
                            this.audioCtrl.setValue(res);
                            break;
                        case 'document':
                        case 'doc':
                            this.docCtrl.setValue(res);
                            const regex: RegExp = /\/posts\/attachments\/(.*)\?X-Amz-Algorithm=/;
                            const match: RegExpMatchArray | null = value.file.match(regex);
                            if (match) {
                                const dotIndex: number = match[1].indexOf('.');
                                this.docTitle.setValue(match[1].substring(0, dotIndex))
                            }
                            setTimeout((): void => {
                                this.commentType = 'doc'
                            });
                            break;
                    }
                    this.isLoaded = true;
                });
            } else if (value?.attachment_type === 'video_url') {
                this.initialFile = value.url;
                this.commentType = 'videoLink';
                this.videoLinkFormControl.setValue(value.url);
                this.checkValidity(this.videoLinkFormControl, 'videoLink')
                this.isLoaded = true;
            } else if (value?.attachment_type === 'gif_url') {
                this.initialFile = {embed_url: value.url};
                this.safeImageLink = videoLinkUpload(value.url, this.domSanitizer)?.link;
                this.commentType = 'gif';
                this.selectGif({embed_url: value.url});
                this.isLoaded = true;
            } else if (value?.attachment_type === 'survey_url' && value.url) {
                this.initialFile = {survey_url: value.url};
                // @ts-ignore
                this.surveyFormControl.setValue(value?.url);
                this.checkValidity(this.surveyFormControl, 'survey')
                this.safeImageLink = videoLinkUpload(value.url, this.domSanitizer)?.link;
                this.commentType = "survey_url";
                this.isLoaded = true;
            } else if (value?.attachment_type === 'url' && value.url) {
                this.initialFile = {url: value.url};
                // @ts-ignore
                this.surveyFormControl.setValue(value?.url);
                this.checkValidity(this.surveyFormControl, 'url')
                this.safeImageLink = videoLinkUpload(value.url, this.domSanitizer)?.link;
                this.commentType = "url";
                this.isLoaded = true;
            }

        } else if (!isPoll(value)) {
            this.initialHeatmap = value;
            this.commentType = 'heatmap';
            this.isValid.emit(true);
            this.isLoaded = true;
        } else {
            this.initialPoll = value;
            this.commentType = 'poll';
            this.onToggleGroupChange('poll')
            this.isValid.emit(true);
            this.isLoaded = true;
        }
    }
}

